'use client';

import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { PhoneUpdateInput } from './_components/appointment-items/phone-update-input.component';
import { getAppointmentLocation } from './_services/get-appointment-location.service';

import { CardAppointmentHeader } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/card-appointment-header.component';
import { DetailsAccordion } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/details-accordion.component';
import { EditableField } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/editable-field.component';
import { GuideCodeField } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/guide-code-field.component';
import { LocationBlock } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_primitives/location-block.component';
import { AppointmentCardActions } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment-actions.component';
import { AppointmentCardExpanded } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment-expanded.component';
import { CardAppointmentProps } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/card-appointment.component';
import { Card } from '@/components/ui/card.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { dateFormatter } from '@/utils/formatters/date';
import { formatClientName } from '@/utils/formatters/format-to-title-case';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { getProfessionalProfilePath } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/_utils/appointment-entity-path';

export function CardAppointmentArrivalOrder({ appointment, defaultOpen, onAppointmentUpdate }: CardAppointmentProps) {
  const procedureName = appointment.items.map((item) => item.procedure.name).join(', ');
  const patientName = formatClientName(appointment.patient);
  const professionalName = professionalDisplayName(appointment.professional);
  const location = getAppointmentLocation(appointment);

  const day = dateFormatter(appointment.date, 'date');
  const hour = dateFormatter(appointment.date, 'HH:mm');
  const endHour = appointment.end ? dateFormatter(appointment.end, 'HH:mm') : null;
  const formattedDate = endHour
    ? `${day} das ${hour} às ${endHour} (Horário de Brasília), por ordem de chegada`
    : `${day} às ${hour} (Horário de Brasília)`;

  return (
    <AppointmentCardActions appointment={appointment} onAppointmentUpdate={onAppointmentUpdate}>
      {({ onEditDate, onEditPatient }) => (
        <Card
          data-testid={DataTestIdEnum.APPOINTMENT_CARD_ARRIVAL_ORDER}
          className="flex flex-col gap-4 rounded-2xl p-4 w-full"
        >
          <div className="flex flex-col gap-4">
            <CardAppointmentHeader
              avatarSrc={appointment.professional?.avatar}
              avatarAlt={professionalName}
              avatarType={EntityTypeEnum.PROFESSIONAL}
              name={professionalName}
              href={getProfessionalProfilePath(appointment.professional)}
            />

            <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">{procedureName}</Typography>

            <hr className="border-t border-gray-200 w-full" />

            <GuideCodeField appointment={appointment} />

            <EditableField
              label="Data e hora"
              value={formattedDate}
              onEdit={onEditDate}
              editAriaLabel="Editar data"
              editTestId={`${DataTestIdEnum.APPOINTMENT_CARD_EDIT_DATE}-${appointment.id}`}
            />

            <EditableField
              label="Atendimento para"
              value={patientName}
              onEdit={onEditPatient}
              editAriaLabel="Editar paciente"
              editTestId={`${DataTestIdEnum.APPOINTMENT_CARD_EDIT_PATIENT}-${appointment.id}`}
            />

            <LocationBlock {...location} />

            <PhoneUpdateInput defaultValue={appointment.customerContactPhone} />
          </div>

          <DetailsAccordion defaultOpen={defaultOpen}>
            <AppointmentCardExpanded
              appointment={appointment}
              onEditPatient={onEditPatient}
              onEditDate={onEditDate}
            />
          </DetailsAccordion>
        </Card>
      )}
    </AppointmentCardActions>
  );
}
